<script lang="ts" setup>
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-vue-next'
import { FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import SocialLogin from './Account/SocialLogin.vue'
import { vAutoAnimate } from '@formkit/auto-animate'

defineProps({
  useSocialLogin: {
    type: Boolean,
    required: true
  }
})


const client = useSupabaseClient()
const loading = ref(false)
const showPassword = ref(false)

const formSchema = toTypedSchema(z.object({
  email: z.string().email(),
  password: z.string().min(8),
}))


const apiErrors = ref({
  wrongCredentials: false,
  Provider: false,
  accountNotVerified: false,
  accountSuspended: false,
  noAccess: false,
  alreadyRegistered: false
})

const apiErrorMessages = computed(() => {
  return {
    email: apiErrors.value.wrongCredentials ? 'Incorrect username or password' : apiErrors.value.accountSuspended ? 'Account suspended' : apiErrors.value.noAccess ? 'Please email hi@superseeded.ai for access' : apiErrors.value.alreadyRegistered ? 'User already registered' : '',
    password: apiErrors.value.accountNotVerified ? 'Account not verified' : ''
    // Add more here as needed
  };
});

const { validate, errors, resetForm, handleSubmit, setErrors, meta: formMeta } = useForm({
  validationSchema: formSchema
})


const login = handleSubmit(async (values) => {

  loading.value = true

  const validator = await validate();
  if (validator.valid) {
    const { error } = await client.auth.signUp({
      email: values.email,
      password: values.password,
      options: {
        emailRedirectTo: `${window.location.origin}/confirm`,
      },
    })
    // loading.value=false        
    if (error) {
      loading.value = false
      console.log(error)
      // set a manual message manually
      apiErrors.value.noAccess = error.message === "Database error saving new user"
      apiErrors.value.accountNotVerified = error.message === "This email has not been verified"
      apiErrors.value.alreadyRegistered = error.message === "User already registered"
    }
    // Set the error messages
    if (apiErrors.value.noAccess || apiErrors.value.accountNotVerified || apiErrors.value.alreadyRegistered) {
      setErrors({
        email: apiErrorMessages.value.email,
        password: apiErrorMessages.value.password
      })
    }

  }
})

</script>

<template>
  <form @submit="login" :validation-schema="formSchema" class="py-2">
    <FormField v-slot="{ componentField }" name="email">
      <FormItem v-auto-animate>
        <!-- <FormLabel>Email</FormLabel> -->
        <FormControl>
          <Input v-bind="componentField" :disabled="loading" autocomplete="email" type="text" placeholder="Email" />
        </FormControl>
        <FormMessage class="font-light ml-3" />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField }" name="password">
      <FormItem v-auto-animate class="py-2">
        <!-- <FormLabel>Password</FormLabel> -->
        <FormControl>
          <Input v-bind="componentField" :disabled="loading" autocomplete="current-password"
            :type="showPassword ? 'text' : 'password'" placeholder="Password" />
          <button type="button" @click="() => { showPassword = !showPassword }"
            class="absolute end-0 top-0 min-h-9 items-center" variant="outline" size="icon">
            <span class=" inset-y-0 px-2">
              <icon :name="showPassword ? 'lucide:eye' : 'lucide:eye-off'"
                class="w-6 h-4 bg-background text-muted-foreground" />
            </span>
          </button>
        </FormControl>
        <FormMessage class="font-light ml-3" />
      </FormItem>
    </FormField>

    <div class="grid grid-cols-1 gap-4 py-3">



      <Button :disabled="errors.email || errors.password" type="submit" class="text-[1em]">
        <Loader2 v-if="loading" class="w-4 h-4 mr-2 animate-spin" />
        Sign Up
      </Button>

      <!-- <div  class="w-full text-center font-semibold">OR</div> -->
      <SocialLogin v-if="useSocialLogin" />
      <p class="text-xs text-muted-foreground">
        By signing up you agree to our
        <span class="!underline inline-block !cursor-pointer">
          <TermsOfService>
            <span>Terms of Service</span>
          </TermsOfService>
        </span>
        and
        <span class="!underline inline-block !cursor-pointer">
          <PrivacyPolicy>
            <span>Privacy Policy</span>
          </PrivacyPolicy>
        </span>

      </p>



    </div>
  </form>
</template>




<style scoped></style>
